import { LIMIT_SIZE_1000 } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { sqModelsApi } from '@/sdk/api/ModelsApi';
import { setModelVersionState } from '@/tools/models/model.actions';
import { fetchModel } from '@/tools/models/model.utilities';
import type { ModelInputV1 } from '@/sdk/model/ModelInputV1';
import type { ModelType, ModelVersionMetadata } from '@/tools/models/model.types';

/** Edits a specific model version and publishes it as the new production version. */
export const editAndPublishModel = async (modelId: string, versionId: string, input: ModelInputV1) => {
  await sqModelsApi.editModelVersion(input, { modelId, versionId });
  await sqModelsApi.publishModelVersion({ versionId }, { modelId });
};

/** Saves changes to a draft without locking or publishing. */
export const saveDraft = async (modelId: string, versionId: string, input: ModelInputV1) => {
  await sqModelsApi.editModelVersion(input, { modelId, versionId });
};

/** Fetches a model for editing, populating store state and marking inputs as collapsed.
 * Transparently creates a draft so edits are versioned. If a draft already exists it is
 * reused; if the model has no version history yet the initial unlocked model is loaded directly.
 */
export const fetchModelForEditing = async (id: string, modelType: ModelType) => {
  // Try to resume an existing draft first
  try {
    const { data: draft } = await sqModelsApi.getDraft({ modelId: id });
    await fetchModel(draft.versionId!, modelType, true, true);
    await populateSourceVersionNumber(id, modelType);
    return;
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status !== 404) {
      throw error;
    }
  }

  // No draft exists — create one
  try {
    const { data: draft } = await sqModelsApi.createDraft({ modelId: id });
    await fetchModel(draft.versionId!, modelType, true, true);
    await populateSourceVersionNumber(id, modelType);
  } catch (error) {
    const status = (error as any)?.response?.status;
    if (status === 400) {
      // No version history yet — edit the initial unlocked model directly
      await fetchModel(id, modelType, true, true);
      setModelVersionState(modelType, { isLocked: false, isDraft: false, sourceVersionNumber: undefined });
      return;
    }
    throw error;
  }
};

/**
 * Fetches the version list for a model container and sets the source version
 * number (production version) on the store. Called after loading a draft for editing.
 */
async function populateSourceVersionNumber(modelId: string, modelType: ModelType) {
  try {
    const { data } = await sqModelsApi.getModelVersions({ modelId });
    const productionVersion = data.versions.find((v) => v.production);
    setModelVersionState(modelType, {
      isLocked: false,
      isDraft: true,
      sourceVersionNumber: productionVersion?.version,
    });
  } catch {
    // If version fetch fails, still mark as draft
    setModelVersionState(modelType, { isLocked: false, isDraft: true, sourceVersionNumber: undefined });
  }
}

/**
 * Fetches version and draft metadata for a single model container.
 * Returns null if version data cannot be fetched (e.g. no Locking license).
 */
export const fetchModelVersionMetadata = async (modelId: string): Promise<ModelVersionMetadata> => {
  const [versionsResult, draftResult] = await Promise.allSettled([
    sqModelsApi.getModelVersions({ modelId }),
    sqModelsApi.getDraft({ modelId }),
  ]);

  const versions = versionsResult.status === 'fulfilled' ? versionsResult.value.data.versions : [];

  const hasDraft = draftResult.status === 'fulfilled';
  const draftVersionId = hasDraft ? draftResult.value.data.versionId : undefined;

  const productionVersion = versions.find((v) => v.production);

  return {
    hasDraft,
    draftVersionId,
    productionVersionNumber: productionVersion?.version,
    versionCount: versions.length,
    isLocked: versions.length > 0,
    versions,
  };
};

/**
 * Fetches version and draft metadata for multiple model containers in parallel.
 * Returns a map of modelId -> ModelVersionMetadata.
 */
export const fetchAllModelVersionMetadata = async (modelIds: string[]): Promise<Map<string, ModelVersionMetadata>> => {
  const results = await Promise.allSettled(
    modelIds.map((id) => fetchModelVersionMetadata(id).then((metadata) => ({ id, metadata }))),
  );

  const metadataMap = new Map<string, ModelVersionMetadata>();
  for (const result of results) {
    if (result.status === 'fulfilled') {
      metadataMap.set(result.value.id, result.value.metadata);
    }
  }
  return metadataMap;
};

/** Fetches model container items scoped to the given workbook IDs. */
export async function fetchModelItems(workbookIds: string[]) {
  const types = [SeeqNames.Types.ModelContainer];
  const filters = [
    `${SeeqNames.Properties.DatasourceClass}==${SeeqNames.LocalDatasources.Models.DatasourceClass} && ${SeeqNames.Properties.DatasourceId}==${SeeqNames.LocalDatasources.Models.User.DatasourceId}`,
  ];
  const { data } = await sqItemsApi.searchItems({
    types,
    filters,
    scope: workbookIds,
    limit: LIMIT_SIZE_1000,
  });
  return data.items ?? [];
}
